import React from 'react';
import moment from 'moment';
// Helpers
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
import { isCopyItem, isFileItem, isFilesItem } from 'helpers/deliverables';
import { getContentTypeText } from 'helpers/socialMediaContentTypes';
// Types
import {
  CBDeliverableStatus,
  CbDeliverableV2Status,
  CBDeliverableItemType,
  CbDeliverableItemV2Status,
} from 'api/graphql-global-types';
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverables,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_files,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2_files,
} from 'api/campaignV2/types/GetCampaignOffers';
// Styles
import styles from './CampaignDeliverablesRevisionHistory.module.scss';

type CampaignDeliverablesRevisionHistoryProps = {
  deliverables:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables[]
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2[];
  isV2Campaign: boolean;
  isV3Campaign?: boolean;
};

// Helper functions for V3 items
const isCopyItemV3 = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2 => {
  return item.type === CBDeliverableItemType.COPY;
};

const isFileItemV3 = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2 => {
  return item.type === CBDeliverableItemType.FILE;
};

const FilePreview = ({
  file,
}: {
  file:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_files
    | GetCampaignOffers_getCampaignOffers_entities_deliverables_items_CBDeliverableFileItem
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2_files;
}): JSX.Element => {
  const isImage = file.key?.match(/\.(jpeg|jpg|png)$/i);
  const isVideo = file.key?.match(/\.(mp4)$/i);
  const url = file.url || '';
  const originalUrl = 'originalUrl' in file ? file.originalUrl : null;
  const downloadUrl = originalUrl || url;

  return (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
    >
      {isImage && 'Image  '}
      {isVideo && 'Video  '}
      {file.name}
    </a>
  );
};

const RevisionHistoryV1 = ({
  files,
}: {
  files: GetCampaignOffers_getCampaignOffers_entities_deliverables_files[];
}): JSX.Element => {
  return (
    <>
      <p>Uploaded Media:</p>
      <div className={styles.mediaWrapper}>
        {files.map((file, fileIndex) => (
          <FilePreview key={file.key + '_' + fileIndex} file={file} />
        ))}
      </div>
    </>
  );
};

const RevisionHistoryV2 = ({
  items,
}: {
  items: GetCampaignOffers_getCampaignOffers_entities_deliverables_items[];
}): JSX.Element => {
  return (
    <>
      <p>Deliverables:</p>
      <div className={styles.mediaWrapper}>
        {items.map((item, index) => {
          const deliverableNumber = index + 1;
          const itemName = item.task?.title || 'Untitled Task';
          const description = item.task?.description || '';
          const itemType = getContentTypeText(item.task);

          return (
            <div key={item.id} className={styles.v2Item}>
              <div className={styles.itemHeader}>
                <strong>
                  Deliverable {deliverableNumber} - {itemName}
                </strong>
              </div>
              {itemType && <div>Type: {itemType}</div>}
              {description && (
                <div className={styles.itemDescription}>
                  Description: {description}
                </div>
              )}
              {isFileItem(item) && item.key && item.url ? (
                <FilePreview file={item} />
              ) : isCopyItem(item) ? (
                <span>
                  Post Copy:{' '}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: parseForInnerHTMLWithBreaks(item.copy),
                    }}
                  />
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const RevisionHistoryV3 = ({
  items,
  revisions = [],
}: {
  items: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items[];
  revisions?: any[];
}): JSX.Element => {
  const getFeedbackForItem = (itemId: string) => {
    if (!revisions || revisions.length === 0) return null;

    const latestRevision = revisions[revisions.length - 1];
    if (!latestRevision || !latestRevision.items) return null;

    const feedbackItem = latestRevision.items.find(
      (revisionItem: any) => revisionItem.itemId === itemId
    );

    return feedbackItem?.feedback || null;
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className={styles.itemStatusApproved}>Status: Approved</span>
        );
      case 'REJECTED':
        return (
          <span className={styles.itemStatusRejected}>
            Status: Changes Needed
          </span>
        );
      case 'ON_BRAND_REVIEW':
        return (
          <span className={styles.itemStatusOnReview}>Status: On Review</span>
        );
      case 'NOT_SUBMITTED':
        return (
          <span className={styles.itemStatusNotSubmitted}>
            Status: Not Submitted
          </span>
        );
      default:
        return (
          <span className={styles.itemStatusDefault}>Status: {status}</span>
        );
    }
  };

  return (
    <>
      <p>Deliverables:</p>
      <div className={styles.mediaWrapper}>
        {items.map((item, index) => {
          console.log('item', item);
          const deliverableNumber = index + 1;
          const itemName = item.task?.title || 'Untitled Task';
          const description = item.task?.description || '';
          const itemType = getContentTypeText(item.task);
          const feedback = getFeedbackForItem(item.id);

          return (
            <div key={item.id} className={styles.v2Item}>
              <div className={styles.itemHeader}>
                <strong>
                  Deliverable {deliverableNumber} - {itemName}
                </strong>
              </div>
              {/* Status display */}
              <div className={styles.itemStatus}>
                {getStatusDisplay(item.status)}
              </div>
              {/* Feedback for rejected items */}
              {item.status === CbDeliverableItemV2Status.REJECTED && feedback && (
                <div className={styles.itemFeedback}>
                  <strong>Feedback:</strong>{' '}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: parseForInnerHTMLWithBreaks(feedback),
                    }}
                  />
                </div>
              )}
              {itemType && <div>Type: {itemType}</div>}
              {description && (
                <div className={styles.itemDescription}>
                  Description: {description}
                </div>
              )}

              {/* File or Copy content */}
              {isFileItemV3(item) && item.key && item.url ? (
                <div>
                  File: <FilePreview file={item} />
                </div>
              ) : isFilesItem(item) &&
                item.files.length > 0 &&
                item.files[0].key &&
                item.files[0].url ? (
                <div>
                  File: <FilePreview file={item.files[0]} />
                </div>
              ) : isCopyItemV3(item) ? (
                <div>
                  Caption:{' '}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: parseForInnerHTMLWithBreaks(item.copy),
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const CampaignDeliverablesRevisionHistory = ({
  deliverables,
  isV2Campaign,
  isV3Campaign = false,
}: CampaignDeliverablesRevisionHistoryProps): JSX.Element | null => {
  const renderDeliverableItem = (
    item:
      | GetCampaignOffers_getCampaignOffers_entities_deliverables
      | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
    index: number
  ) => {
    // Handle V3 status mapping
    const isV3Item =
      isV3Campaign &&
      'status' in item &&
      typeof item.status === 'string' &&
      Object.values(CbDeliverableV2Status).includes(
        item.status as CbDeliverableV2Status
      );

    let isApproved = false;
    let isRejected = false;
    let isOnReview = false;
    let agencyFeedback = '';

    if (isV3Item) {
      const v3Item =
        item as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
      isApproved = v3Item.status === CbDeliverableV2Status.CONTENT_APPROVED;
      isRejected = v3Item.status === CbDeliverableV2Status.CONTENT_REJECTED;
      isOnReview = v3Item.status === CbDeliverableV2Status.ON_BRAND_REVIEW;
      // V3 doesn't have agencyFeedback in the same structure, using revisions instead
      agencyFeedback = '';
    } else {
      const v2Item =
        item as GetCampaignOffers_getCampaignOffers_entities_deliverables;
      isApproved = v2Item.status === CBDeliverableStatus.APPROVED;
      isRejected = v2Item.status === CBDeliverableStatus.REJECTED;
      isOnReview = v2Item.status === CBDeliverableStatus.ON_REVIEW;
      agencyFeedback = v2Item.agencyFeedback || '';
    }

    return (
      <div className={styles.itemWrapper} key={index}>
        <div className={styles.titleRow}>
          {item.id === deliverables[0].id ? (
            <p className={styles.title}>Original</p>
          ) : (
            <p className={styles.title}>Revision #{index}</p>
          )}
          {isApproved && <p className={styles.approved}>Approved </p>}
          {isRejected && (
            <p className={styles.rejected}>Request Improvement </p>
          )}
          {isOnReview && <p className={styles.onReview}>On Review </p>}
        </div>
        <div className={styles.dateRow}>
          <p className={styles.dateLabel}>Date:</p>
          <p className={styles.dateValue}>
            {moment(item.createdAt).format('MM/DD/YYYY')}
          </p>
        </div>
        {isRejected && agencyFeedback && (
          <>
            <p>Brand Feedback:</p>
            <p
              className={styles.feedback}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(agencyFeedback),
              }}
            />
          </>
        )}
        {isV3Campaign ? (
          <RevisionHistoryV3
            items={
              (
                item as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
              ).items
            }
            revisions={
              (
                item as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
              ).revisions
            }
          />
        ) : isV2Campaign ? (
          <RevisionHistoryV2
            items={
              (
                item as GetCampaignOffers_getCampaignOffers_entities_deliverables
              ).items
            }
          />
        ) : (
          <RevisionHistoryV1
            files={
              (
                item as GetCampaignOffers_getCampaignOffers_entities_deliverables
              ).files
            }
          />
        )}
      </div>
    );
  };

  // V3 Campaign
  if (isV3Campaign) {
    return (
      <div className={styles.root}>
        <div className={styles.reviews}>
          {(
            deliverables as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2[]
          ).map(renderDeliverableItem)}
        </div>
      </div>
    );
  }

  // V2 Campaign
  if (isV2Campaign) {
    return (
      <div className={styles.root}>
        <div className={styles.reviews}>
          {(
            deliverables as GetCampaignOffers_getCampaignOffers_entities_deliverables[]
          ).map(renderDeliverableItem)}
        </div>
      </div>
    );
  }

  // V1 Campaign (default)
  return (
    <div className={styles.root}>
      <div className={styles.reviews}>
        {(
          deliverables as GetCampaignOffers_getCampaignOffers_entities_deliverables[]
        ).map(renderDeliverableItem)}
      </div>
    </div>
  );
};

export default CampaignDeliverablesRevisionHistory;
